import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { io } from 'socket.io-client';
import { restaurantApi, API_HOST } from '../../services/api';
import toast from 'react-hot-toast';

const imgUrl = (path) => path ? `${API_HOST}${path}` : null;

const STATUS_FLOW = {
  pending:   { label: 'Gözləyir',      next: 'confirmed',  nextLabel: 'Qəbul Et',           color: '#f59e0b', bg: '#f59e0b22' },
  confirmed: { label: 'Qəbul edildi',  next: 'preparing',  nextLabel: 'Hazırlamağa Başla',  color: '#3b82f6', bg: '#3b82f622' },
  preparing: { label: 'Hazırlanır',    next: 'ready',      nextLabel: 'Hazırdır',           color: '#8b5cf6', bg: '#8b5cf622' },
  ready:     { label: 'Hazırdır',      next: 'served',     nextLabel: 'Verildi',            color: '#10b981', bg: '#10b98122' },
  served:    { label: 'Verildi',       next: null,         nextLabel: null,                 color: '#475569', bg: '#47556922' },
};

export default function Kitchen() {
  const { id } = useParams();
  const [orders, setOrders]           = useState([]);
  const [loading, setLoading]         = useState(true);
  const [wsStatus, setWsStatus]       = useState('connecting');
  const [waiterAlerts, setWaiterAlerts] = useState([]);
  const [view, setView]               = useState('kanban'); // 'kanban' | 'table'
  const [restaurant, setRestaurant]   = useState(null);
  const [itemImgMap, setItemImgMap]   = useState({});
  const socketRef    = useRef(null);
  const reconnectRef = useRef(null);

  const loadOrders = useCallback(async () => {
    try {
      const d = await restaurantApi.kitchenOrders(id);
      setOrders(Array.isArray(d) ? d : (d?.data || []));
    } catch {} finally { setLoading(false); }
  }, [id]);

  useEffect(() => {
    restaurantApi.getMenu(id).then(data => {
      if (data?.restaurant) setRestaurant(data.restaurant);
      if (data?.categories) {
        const map = {};
        data.categories.forEach(cat => {
          cat.items?.forEach(item => { if (item.imageUrl) map[item.id] = item.imageUrl; });
        });
        setItemImgMap(map);
      }
    }).catch(() => {});
  }, [id]);

  useEffect(() => {
    loadOrders();

    const connect = () => {
      try {
        const apiBase = (process.env.REACT_APP_API_URL || 'http://localhost:3000/api/v1').replace(/\/api\/v1\/?$/, '');
        const socket  = io(`${apiBase}/kitchen`, { transports: ['polling', 'websocket'], upgrade: true });
        socketRef.current = socket;

        socket.on('connect', () => {
          setWsStatus('connected');
          socket.emit('join', id);
        });

        socket.on('newOrder', (order) => {
          setOrders(prev => [order, ...prev.filter(o => o.id !== order.id)]);
          toast.success('Yeni sifariş gəldi!');
        });

        socket.on('orderUpdate', (order) => {
          setOrders(prev => prev.map(o => o.id === order.id ? order : o)
            .filter(o => !['served', 'cancelled'].includes(o.status)));
        });

        socket.on('waiterCall', (data) => {
          const alert = { id: Date.now(), tableNumber: data.tableNumber, orderId: data.orderId, time: data.time };
          setWaiterAlerts(prev => [alert, ...prev.slice(0, 4)]);
          toast(`🔔 Masa ${data.tableNumber} ofisiant istəyir!`, {
            duration: 8000, style: { background: '#f59e0b', color: '#000', fontWeight: 700 },
          });
        });

        socket.on('disconnect', () => {
          setWsStatus('disconnected');
          reconnectRef.current = setTimeout(connect, 3000);
        });

        socket.on('connect_error', () => setWsStatus('polling'));
      } catch { setWsStatus('polling'); }
    };

    connect();

    const pollInterval = setInterval(() => {
      if (wsStatus === 'polling') loadOrders();
    }, 5000);

    return () => {
      socketRef.current?.disconnect();
      clearTimeout(reconnectRef.current);
      clearInterval(pollInterval);
    };
  }, [id, loadOrders]);

  const updateStatus = async (orderId, status) => {
    try {
      await restaurantApi.updateOrderStatus(orderId, status);
      if (wsStatus !== 'connected') {
        if (['served', 'cancelled'].includes(status)) {
          setOrders(prev => prev.filter(o => o.id !== orderId));
        } else {
          setOrders(prev => prev.map(o => o.id === orderId ? { ...o, status } : o));
        }
        toast.success('Status yeniləndi');
      }
    } catch { toast.error('Xəta'); }
  };

  const grouped = {
    pending:   orders.filter(o => o.status === 'pending'),
    confirmed: orders.filter(o => o.status === 'confirmed'),
    preparing: orders.filter(o => o.status === 'preparing'),
    ready:     orders.filter(o => o.status === 'ready'),
  };

  // Group by table for table view
  const byTable = orders.reduce((acc, o) => {
    const key = o.tableNumber || '—';
    if (!acc[key]) acc[key] = [];
    acc[key].push(o);
    return acc;
  }, {});

  const WsIndicator = () => {
    const cfg = {
      connected:    { color: '#10b981', text: '⚡ Real-time' },
      polling:      { color: '#f59e0b', text: '↻ 5 san.' },
      connecting:   { color: '#3b82f6', text: '◌ Qoşulur' },
      disconnected: { color: '#ef4444', text: '✕ Kəsildi' },
    }[wsStatus] || { color: 'var(--text3)', text: '?' };
    return (
      <div style={{ ...K.badge, color: cfg.color, background: cfg.color + '22', borderColor: cfg.color + '44' }}>
        {cfg.text}
      </div>
    );
  };

  return (
    <div style={K.root}>
      <div style={K.header}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          {restaurant?.logoUrl && (
            <img src={imgUrl(restaurant.logoUrl)} alt=""
              style={{ width: 44, height: 44, borderRadius: 8, objectFit: 'cover', border: '1px solid var(--border)' }} />
          )}
          <h1 style={K.title}>{restaurant?.name || 'Mətbəx Ekranı'}</h1>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          {/* View toggle */}
          <div style={K.viewToggle}>
            {[['kanban', '⊞ Kanban'], ['table', '⊟ Masa']].map(([v, l]) => (
              <button key={v} onClick={() => setView(v)}
                style={{ ...K.viewBtn, ...(view === v ? K.viewBtnActive : {}) }}>
                {l}
              </button>
            ))}
          </div>
          <WsIndicator />
        </div>
      </div>

      {/* Stats bar */}
      <div style={K.statsBar}>
        {Object.entries(grouped).map(([status, ords]) => {
          const s = STATUS_FLOW[status];
          return (
            <div key={status} style={{ ...K.statChip, borderColor: s.color + '44', background: s.bg }}>
              <span style={{ color: s.color, fontWeight: 700 }}>{ords.length}</span>
              <span style={{ color: 'var(--text2)', fontSize: 12 }}> {s.label}</span>
            </div>
          );
        })}
        <div style={{ ...K.statChip, borderColor: 'var(--border)', background: 'var(--card)', marginLeft: 'auto' }}>
          <span style={{ fontWeight: 700 }}>{orders.length}</span>
          <span style={{ color: 'var(--text2)', fontSize: 12 }}> aktiv</span>
        </div>
      </div>

      {/* Waiter call alerts */}
      {waiterAlerts.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          {waiterAlerts.map(a => (
            <div key={a.id} style={K.waiterAlert}>
              <span style={{ color: '#f59e0b', fontWeight: 700, fontSize: 14 }}>
                🔔 Masa {a.tableNumber} — Ofisiant istəyir!
              </span>
              <button onClick={() => setWaiterAlerts(prev => prev.filter(x => x.id !== a.id))}
                style={{ background: 'none', border: 'none', color: '#f59e0b', cursor: 'pointer', fontSize: 18 }}>
                ✕
              </button>
            </div>
          ))}
        </div>
      )}

      {loading ? <div style={K.loading}>Yüklənir...</div> : (

        view === 'kanban' ? (
          // ── Kanban View ──
          <div style={K.columns}>
            {Object.entries(grouped).map(([status, ords]) => {
              const s = STATUS_FLOW[status];
              return (
                <div key={status} style={K.column}>
                  <div style={{ ...K.colHead, borderBottom: `2px solid ${s.color}`, color: s.color }}>
                    {s.label} <span style={K.count}>{ords.length}</span>
                  </div>
                  {ords.length === 0
                    ? <div style={K.colEmpty}>Boş</div>
                    : ords.map(o => <OrderCard key={o.id} order={o} onUpdate={updateStatus} itemImgMap={itemImgMap} />)}
                </div>
              );
            })}
          </div>
        ) : (
          // ── Table View ──
          Object.keys(byTable).length === 0
            ? <div style={{ ...K.colEmpty, textAlign: 'center', padding: 48 }}>Aktiv sifariş yoxdur</div>
            : <div style={K.tableGrid}>
                {Object.entries(byTable)
                  .sort(([a], [b]) => a.localeCompare(b, undefined, { numeric: true }))
                  .map(([tableNum, tableOrders]) => (
                    <div key={tableNum} style={K.tableCard}>
                      <div style={K.tableCardHead}>
                        <span style={K.tableNumLarge}>🍽 Masa {tableNum}</span>
                        <span style={K.tableOrderCount}>{tableOrders.length} sifariş</span>
                      </div>
                      {tableOrders.map(o => (
                        <div key={o.id} style={K.tableOrderRow}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                            <StatusChip status={o.status} />
                            <ElapsedTime createdAt={o.createdAt} />
                          </div>
                          <div style={{ marginBottom: 8 }}>
                            {o.items?.map((item, i) => (
                              <div key={i} style={{ display: 'flex', gap: 8, padding: '3px 0', borderBottom: '1px solid var(--border)', fontSize: 13 }}>
                                <span style={{ color: 'var(--primary)', fontWeight: 700 }}>{item.quantity}×</span>
                                <span style={{ color: 'var(--text)', flex: 1 }}>{item.itemName}</span>
                                {item.notes && <span style={{ color: 'var(--warning)', fontSize: 11 }}>{item.notes}</span>}
                              </div>
                            ))}
                          </div>
                          {o.notes && <div style={{ fontSize: 12, color: 'var(--warning)', background: 'var(--warning)11', borderRadius: 6, padding: '4px 8px', marginBottom: 8 }}>Not: {o.notes}</div>}
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--success)' }}>{Number(o.totalAmount).toFixed(2)} AZN</span>
                            {STATUS_FLOW[o.status]?.next && (
                              <button
                                style={{ ...K.nextBtn, background: STATUS_FLOW[o.status].color }}
                                onClick={() => updateStatus(o.id, STATUS_FLOW[o.status].next)}>
                                {STATUS_FLOW[o.status].nextLabel} →
                              </button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  ))}
              </div>
        )
      )}
    </div>
  );
}

function StatusChip({ status }) {
  const s = STATUS_FLOW[status] || STATUS_FLOW.pending;
  return (
    <span style={{ fontSize: 11, fontWeight: 700, padding: '3px 8px', borderRadius: 20, background: s.bg, color: s.color, textTransform: 'uppercase' }}>
      {s.label}
    </span>
  );
}

function ElapsedTime({ createdAt }) {
  const mins = Math.floor((Date.now() - new Date(createdAt)) / 60000);
  const urgent = mins > 15;
  return (
    <span style={{ fontSize: 12, fontWeight: 700, color: urgent ? 'var(--danger)' : 'var(--text3)' }}>
      {mins} dəq {urgent && '⚠'}
    </span>
  );
}

function OrderCard({ order, onUpdate, itemImgMap = {} }) {
  const s    = STATUS_FLOW[order.status] || STATUS_FLOW.pending;
  const mins = Math.floor((Date.now() - new Date(order.createdAt)) / 60000);
  const urgent = mins > 15;

  return (
    <div style={{ ...K.card, borderTop: `3px solid ${s.color}`, ...(urgent ? { boxShadow: '0 0 0 1px #ef444444' } : {}) }}>
      <div style={K.cardHead}>
        <span style={K.tableNum}>Masa {order.tableNumber || '—'}</span>
        <span style={{ ...K.statusBadge, background: s.bg, color: s.color }}>{s.label}</span>
        <span style={{ ...K.time, color: urgent ? 'var(--danger)' : 'var(--text3)' }}>{mins}dəq {urgent && '⚠'}</span>
      </div>
      <div style={K.items}>
        {order.items?.map((item, i) => (
          <div key={i} style={K.item}>
            {itemImgMap[item.menuItemId] && (
              <img src={imgUrl(itemImgMap[item.menuItemId])} alt=""
                style={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 6, flexShrink: 0 }} />
            )}
            <span style={K.qty}>{item.quantity}×</span>
            <span style={K.iName}>{item.itemName}</span>
            {item.notes && <span style={K.note}>{item.notes}</span>}
          </div>
        ))}
      </div>
      {order.notes && <div style={K.orderNote}>Not: {order.notes}</div>}
      <div style={K.total}>{Number(order.totalAmount).toFixed(2)} AZN</div>
      {s.next && (
        <button style={{ ...K.nextBtn, background: s.color }} onClick={() => onUpdate(order.id, s.next)}>
          {s.nextLabel} →
        </button>
      )}
    </div>
  );
}

const K = {
  root:        { background: 'var(--bg)', minHeight: '100vh', padding: 24 },
  header:      { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 },
  title:       { fontSize: 24, fontWeight: 800, color: 'var(--text)' },
  badge:       { fontSize: 12, fontWeight: 700, padding: '6px 14px', borderRadius: 20, border: '1px solid' },
  viewToggle:  { display: 'flex', gap: 4, background: 'var(--card)', borderRadius: 10, padding: 4, border: '1px solid var(--border)' },
  viewBtn:     { background: 'none', border: 'none', borderRadius: 7, padding: '6px 14px', cursor: 'pointer', fontSize: 13, fontWeight: 600, color: 'var(--text3)' },
  viewBtnActive: { background: 'var(--primary)', color: 'white' },
  statsBar:    { display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' },
  statChip:    { display: 'flex', alignItems: 'center', gap: 6, padding: '6px 14px', borderRadius: 20, border: '1px solid', fontSize: 13 },
  waiterAlert: { background: '#f59e0b22', border: '1px solid #f59e0b55', borderRadius: 10, padding: '10px 16px', marginBottom: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  loading:     { textAlign: 'center', color: 'var(--text3)', padding: 80, fontSize: 18 },
  columns:     { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 16 },
  column:      { background: 'var(--card)', borderRadius: 12, padding: 12, minHeight: 400 },
  colHead:     { fontSize: 13, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', padding: '0 0 12px', marginBottom: 12, display: 'flex', justifyContent: 'space-between' },
  count:       { background: 'rgba(255,255,255,0.1)', borderRadius: 12, padding: '2px 8px', fontSize: 12 },
  colEmpty:    { textAlign: 'center', color: 'var(--border)', padding: 32, fontSize: 13 },
  card:        { background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 10, padding: 14, marginBottom: 10 },
  cardHead:    { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 },
  tableNum:    { fontSize: 16, fontWeight: 800, color: 'var(--text)' },
  statusBadge: { fontSize: 11, fontWeight: 600, padding: '3px 8px', borderRadius: 20, textTransform: 'uppercase' },
  time:        { fontSize: 12, fontWeight: 700 },
  items:       { marginBottom: 10 },
  item:        { display: 'flex', alignItems: 'baseline', gap: 6, padding: '4px 0', borderBottom: '1px solid var(--border)' },
  qty:         { fontSize: 14, fontWeight: 700, color: 'var(--primary)', minWidth: 24 },
  iName:       { fontSize: 14, color: 'var(--text)', flex: 1 },
  note:        { fontSize: 11, color: 'var(--warning)', fontStyle: 'italic' },
  orderNote:   { fontSize: 12, color: 'var(--warning)', marginBottom: 8, padding: '6px 8px', background: 'var(--warning)11', borderRadius: 6 },
  total:       { fontSize: 15, fontWeight: 700, color: 'var(--success)', marginBottom: 10, textAlign: 'right' },
  nextBtn:     { width: '100%', border: 'none', borderRadius: 8, padding: '16px 10px', fontWeight: 700, cursor: 'pointer', color: 'white', fontSize: 17, textAlign: 'center' },
  // Table view
  tableGrid:   { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 16 },
  tableCard:   { background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 12, overflow: 'hidden' },
  tableCardHead: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', background: 'var(--primary)18', borderBottom: '1px solid var(--border)' },
  tableNumLarge: { fontSize: 15, fontWeight: 800, color: 'var(--text)' },
  tableOrderCount: { fontSize: 12, color: 'var(--primary)', background: 'var(--primary)22', padding: '2px 8px', borderRadius: 12 },
  tableOrderRow: { padding: '12px 16px', borderBottom: '1px solid var(--border)' },
};
